import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listAgentCampaigns,
  createAgentCampaign,
  pauseAgentCampaign,
  resumeAgentCampaign,
  deleteAgentCampaign,
} from "@/lib/api";
import { toast } from "sonner";
import {
  Bot,
  Play,
  Pause,
  Trash2,
  Plus,
  Sparkles,
  Search,
  Send,
  Activity,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const StatusBadge = ({ status }) => {
  const map = {
    running: "badge-success",
    paused: "badge-warning",
    stopped: "badge-neutral",
  };
  return <span className={`badge ${map[status] || "badge-neutral"}`}>{status}</span>;
};

const DEFAULT_PROMPT =
  "write email to reach new business owners to try a fully ready business receptionist in 2 minutes";

export default function AgentCampaigns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    country: "",
    industry: "",
    prompt: DEFAULT_PROMPT,
    trial_link: "https://botnira.com/",
    rate_per_hour: 30,
    custom_queries: "",
  });

  const refresh = async () => {
    try {
      const data = await listAgentCampaigns();
      setItems(data);
    } catch {
      toast.error("Failed to load campaigns");
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, []);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.country.trim() || !form.industry.trim()) {
      toast.error("Name, country and industry are required");
      return;
    }
    try {
      const payload = {
        ...form,
        custom_queries: (form.custom_queries || "")
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      await createAgentCampaign(payload);
      toast.success("Agent started — searching now");
      setOpen(false);
      setForm({ ...form, name: "", custom_queries: "" });
      refresh();
    } catch {
      toast.error("Failed to create");
    }
  };

  const togglePause = async (c) => {
    try {
      if (c.status === "running") await pauseAgentCampaign(c.id);
      else await resumeAgentCampaign(c.id);
      refresh();
    } catch {
      toast.error("Failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Stop this agent and delete all its leads?")) return;
    try {
      await deleteAgentCampaign(id);
      refresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="agent-campaigns-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="section-label mb-2">AI Outbound Agent</div>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]"
            style={{ fontFamily: "Outfit" }}
          >
            AI Campaigns
          </h1>
          <p className="text-sm text-[#475569] mt-2 max-w-2xl">
            Enter a country + industry. The agent will search the web 24/7 to find real businesses,
            write personalized emails, and send them in Inbox Mode. Pause anytime.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary inline-flex items-center gap-2" data-testid="btn-new-agent">
              <Plus size={15} strokeWidth={2.2} /> Start New Agent
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="dialog-new-agent">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }}>Start AI Outbound Agent</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="block text-sm font-medium mb-1.5">Campaign name</label>
                <input
                  className="field-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. India dental clinics — Feb"
                  data-testid="input-agent-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Country</label>
                  <input
                    className="field-input"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="India"
                    data-testid="input-agent-country"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Industry</label>
                  <input
                    className="field-input"
                    value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    placeholder="dental clinics"
                    data-testid="input-agent-industry"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Email prompt <span className="text-xs text-[#94A3B8] font-normal">(optional guidance for the AI writer)</span>
                </label>
                <textarea
                  className="field-input font-mono text-xs"
                  rows={3}
                  value={form.prompt}
                  onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                  data-testid="textarea-agent-prompt"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Trial link</label>
                  <input
                    className="field-input font-mono text-xs"
                    value={form.trial_link}
                    onChange={(e) => setForm({ ...form, trial_link: e.target.value })}
                    data-testid="input-agent-trial"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    Rate (emails/hour)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    className="field-input"
                    value={form.rate_per_hour}
                    onChange={(e) => setForm({ ...form, rate_per_hour: Number(e.target.value) })}
                    data-testid="input-agent-rate"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Custom search keywords <span className="text-xs text-[#94A3B8] font-normal">(one per line — leave empty to auto-generate)</span>
                </label>
                <textarea
                  className="field-input font-mono text-xs"
                  rows={5}
                  value={form.custom_queries}
                  onChange={(e) => setForm({ ...form, custom_queries: e.target.value })}
                  placeholder={'e.g.\n"dental clinic Mumbai" contact email\ndentist in Delhi "info@"\nchildren dentist Bangalore website'}
                  data-testid="textarea-agent-custom-queries"
                />
                <p className="text-xs text-[#94A3B8] mt-1.5">
                  Tip: include quotes for exact phrases, and terms like &quot;info@&quot;, &quot;contact us&quot;, or a specific city to narrow results.
                </p>
              </div>

              <div className="rounded-md bg-[#F8FAFC] border border-[#E2E8F0] p-3 text-xs text-[#475569] leading-relaxed">
                <Sparkles size={12} className="inline mr-1 text-[#002FA7]" /> Emails are sent in
                <strong> Inbox Mode</strong> (plain text, unsubscribe headers) for best Primary-inbox
                placement. Global suppression is applied so no address is emailed twice.
              </div>
            </div>
            <DialogFooter>
              <button className="btn-secondary" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreate} data-testid="btn-create-agent">
                Start Agent
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-[#94A3B8]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center" data-testid="empty-agents">
          <div className="w-14 h-14 rounded-full bg-[#E5E9F6] flex items-center justify-center mx-auto mb-4">
            <Bot size={22} className="text-[#002FA7]" strokeWidth={2} />
          </div>
          <div className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
            No AI campaigns yet
          </div>
          <p className="text-sm text-[#475569] mt-1 max-w-md mx-auto">
            Start an agent to have it discover leads and send personalized outreach 24/7.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div
              key={c.id}
              className="bg-white border border-[#E2E8F0] rounded-lg p-5"
              data-testid={`agent-card-${c.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-md bg-[#E5E9F6] flex items-center justify-center">
                  <Bot size={18} className="text-[#002FA7]" strokeWidth={2} />
                </div>
                <StatusBadge status={c.status} />
              </div>

              <Link
                to={`/agent-campaigns/${c.id}`}
                className="text-lg font-semibold tracking-tight text-[#0F172A] hover:text-[#002FA7] block"
                style={{ fontFamily: "Outfit" }}
              >
                {c.name}
              </Link>
              <div className="text-xs text-[#94A3B8] mt-0.5">
                {c.industry} · {c.country} · {c.rate_per_hour}/hr
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4">
                <Stat icon={Search} label="Leads" value={c.leads_found} />
                <Stat icon={Send} label="Sent" value={c.emails_sent} />
                <Stat icon={Activity} label="Failed" value={c.emails_failed} tone={c.emails_failed > 0 ? "warn" : ""} />
              </div>

              {c.last_activity && (
                <div className="mt-4 pt-3 border-t border-[#F1F5F9] text-xs text-[#475569] truncate" data-testid={`agent-activity-${c.id}`}>
                  <span className="text-[#94A3B8]">Latest: </span>{c.last_activity}
                </div>
              )}

              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={() => togglePause(c)}
                  className="btn-secondary flex-1 inline-flex items-center justify-center gap-1.5"
                  data-testid={`btn-toggle-agent-${c.id}`}
                >
                  {c.status === "running" ? (
                    <><Pause size={13} /> Pause</>
                  ) : (
                    <><Play size={13} /> Resume</>
                  )}
                </button>
                <Link
                  to={`/agent-campaigns/${c.id}`}
                  className="btn-secondary inline-flex items-center justify-center gap-1.5"
                >
                  View
                </Link>
                <button
                  onClick={() => handleDelete(c.id)}
                  className="p-2 rounded-md text-[#94A3B8] hover:text-[#E11D48] hover:bg-[#FFF1F2]"
                  data-testid={`btn-delete-agent-${c.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className={`rounded-md p-2 ${tone === "warn" ? "bg-[#FEF3C7]" : "bg-[#F8FAFC]"}`}>
    <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">
      <Icon size={10} /> {label}
    </div>
    <div className="text-lg font-semibold text-[#0F172A] tabular-nums" style={{ fontFamily: "Outfit" }}>
      {value}
    </div>
  </div>
);
